import { Component, ReactNode } from 'react';
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from 'react-error-boundary';
import { ConnectionError, ConnectionErrorType } from './ConnectionError';
import { ConnectingScreen } from './ConnectingScreen';
import { healthCheckService, HealthStatus } from '@/services/healthCheck.service';
import { ErrorPhaseManager, ErrorPhase } from '@/services/errorPhase.manager';
import { errorCoordinator } from '@/services/errorCoordinator';

// Phase timing constants
const PHASE_1_DURATION = 45_000; // Silent spinner (45s)
const PHASE_2_DURATION = 45_000; // ConnectingScreen (45s)
const AGGRESSIVE_RETRY_MS = 3_000; // Retry interval phases 1-2
const RELAXED_RETRY_MS = 10_000; // Retry interval phase 3
const NORMAL_POLL_MS = 60_000; // Normal healthy polling

/**
 * Maps health status to connection error type
 */
function mapHealthStatusToErrorType(status: HealthStatus): ConnectionErrorType | null {
  switch (status) {
    case 'backend_down':
      return 'backend';
    case 'database_down':
      return 'database';
    case 'network_error':
      return 'network';
    case 'healthy':
    default:
      return null;
  }
}

/**
 * Custom fallback component for React errors (not health errors)
 */
function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  let errorType: ConnectionErrorType = 'backend';

  if (error.message?.toLowerCase().includes('network')) {
    errorType = 'network';
  } else if (error.message?.toLowerCase().includes('database') || error.message?.toLowerCase().includes('timeout')) {
    errorType = 'database';
  }

  return (
    <ConnectionError
      type={errorType}
      onRetry={resetErrorBoundary}
      autoRetryInterval={30000}
    />
  );
}

// ─── Silent Spinner Overlay ──────────────────────────────────────────────────

function SilentSpinner() {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 14px',
        borderRadius: 8,
        background: 'rgba(13, 13, 26, 0.85)',
        border: '1px solid rgba(244, 211, 94, 0.25)',
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: 12,
        fontFamily: 'monospace',
        pointerEvents: 'none',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: '#F4D35E',
          animation: 'silentPulse 1.5s ease-in-out infinite',
        }}
      />
      <style>{`
        @keyframes silentPulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ─── HealthMonitor (3-phase) ─────────────────────────────────────────────────

interface HealthMonitorState {
  healthStatus: HealthStatus;
  phase: ErrorPhase;
  retryCount: number;
}

/**
 * 3-phase progressive health monitor:
 *   Phase 1 (silent, 45s):  Renders children + subtle spinner overlay
 *   Phase 2 (warning, 45s): ConnectingScreen with retry button
 *   Phase 3 (error):        Full ConnectionError card
 *
 * Retry strategy:
 *   Phases 1-2: aggressive retries every 3s
 *   Phase 3:    relaxed retries every 10s
 *   Recovery:   reset phase, clear UI, resume 60s polling
 */
class HealthMonitor extends Component<{ children: ReactNode }, HealthMonitorState> {
  private unsubscribe?: () => void;
  private retryInterval: ReturnType<typeof setInterval> | null = null;

  private phaseManager = new ErrorPhaseManager({
    phase1Duration: PHASE_1_DURATION,
    phase2Duration: PHASE_2_DURATION,
    onPhaseChange: (phase) => {
      this.setState({ phase });

      // Switch from aggressive to relaxed retries when entering phase 3
      if (phase === 'error') {
        this.startRetries(RELAXED_RETRY_MS);
      }
    },
  });

  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = {
      healthStatus: healthCheckService.getLastStatus(),
      phase: 'silent',
      retryCount: 0,
    };
  }

  componentDidMount() {
    // Start normal polling
    healthCheckService.startMonitoring(NORMAL_POLL_MS);

    // Subscribe to health changes
    this.unsubscribe = healthCheckService.subscribe((status) => {
      const wasUnhealthy = this.state.healthStatus !== 'healthy';
      const isNowHealthy = status === 'healthy';
      const isNowUnhealthy = status !== 'healthy';

      if (isNowUnhealthy && !this.phaseManager.isActive()) {
        // First failure — start phase progression + aggressive retries
        this.phaseManager.start();
        this.startRetries(AGGRESSIVE_RETRY_MS);
        errorCoordinator.setHealthMonitorActive(true);
      }

      if (isNowHealthy && wasUnhealthy) {
        // Recovery — reset everything
        this.phaseManager.reset();
        this.stopRetries();
        errorCoordinator.setHealthMonitorActive(false);
        // Resume normal polling
        healthCheckService.stopMonitoring();
        healthCheckService.startMonitoring(NORMAL_POLL_MS);
      }

      this.setState({
        healthStatus: status,
        phase: isNowHealthy ? 'silent' : this.state.phase,
        retryCount: isNowHealthy ? 0 : this.state.retryCount,
      });
    });
  }

  componentWillUnmount() {
    this.unsubscribe?.();
    this.phaseManager.reset();
    this.stopRetries();
    healthCheckService.stopMonitoring();
  }

  private startRetries(intervalMs: number) {
    this.stopRetries();
    this.retryInterval = setInterval(() => {
      this.setState((prev) => ({ retryCount: prev.retryCount + 1 }));
      healthCheckService.checkHealth();
    }, intervalMs);
  }

  private stopRetries() {
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = null;
    }
  }

  handleRetry = async () => {
    this.setState((prev) => ({ retryCount: prev.retryCount + 1 }));
    const result = await healthCheckService.checkHealth();
    if (result.status === 'healthy') {
      this.setState({ healthStatus: 'healthy', phase: 'silent', retryCount: 0 });
    }
  };

  render() {
    const { healthStatus, phase, retryCount } = this.state;
    const errorType = mapHealthStatusToErrorType(healthStatus);

    // Healthy — render children normally
    if (!errorType) {
      return this.props.children;
    }

    // Phase 1 (silent): render children + subtle spinner overlay
    if (phase === 'silent') {
      return (
        <>
          {this.props.children}
          <SilentSpinner />
        </>
      );
    }

    // Phase 2 (warning): ConnectingScreen
    if (phase === 'warning') {
      return <ConnectingScreen onRetry={this.handleRetry} retryCount={retryCount} />;
    }

    // Phase 3 (error): Full ConnectionError card
    return (
      <ConnectionError
        type={errorType}
        onRetry={this.handleRetry}
        autoRetryInterval={RELAXED_RETRY_MS}
        retryCount={retryCount}
      />
    );
  }
}

/**
 * Main error boundary that combines React error handling with health monitoring
 */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  const handleError = (error: Error, errorInfo: { componentStack: string }) => {
    console.error('Application Error:', error);
    console.error('Component Stack:', errorInfo.componentStack);
  };

  const handleReset = () => {
    healthCheckService.checkHealth();
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleError}
      onReset={handleReset}
    >
      <HealthMonitor>{children}</HealthMonitor>
    </ReactErrorBoundary>
  );
}
